import { useRef, useState } from "react";
import type { Member, MemberAccountTransaction, PaymentMode } from "@/services/types";
import { memberService } from "@/services/members";
import { Modal } from "@/components/base/Modal";
import { Button } from "@/components/base/Button";
import { Select } from "@/components/base/Select";
import { TextField } from "@/components/base/TextField";
import { Textarea } from "@/components/base/Textarea";
import { DatePicker } from "@/components/base/DatePicker";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { useToast } from "@/context/ToastContext";

interface AdvanceWithdrawModalProps {
  open: boolean;
  onClose: () => void;
  member: Member;
  onSuccess: (transaction: MemberAccountTransaction, updatedMember: Member) => void;
}

const WITHDRAW_MODES: { value: PaymentMode; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
];

function todayDateOnly(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function AdvanceWithdrawModal({ open, onClose, member, onSuccess }: AdvanceWithdrawModalProps) {
  const toast = useToast();
  const currentBalance = member.accountBalance ?? 0;
  const [amount, setAmount] = useState<number | null>(null);
  const [mode, setMode] = useState<PaymentMode>("cash");
  const [referenceNo, setReferenceNo] = useState("");
  const [withdrawnOn, setWithdrawnOn] = useState<string>(todayDateOnly());
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const referenceNeeded = mode !== "cash";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!amount || amount <= 0) {
      setError("Please enter a valid withdrawal amount greater than zero.");
      return;
    }
    if (amount > currentBalance) {
      setError(`Withdrawal amount (₹${amount.toLocaleString("en-IN")}) exceeds available balance (₹${currentBalance.toLocaleString("en-IN")}).`);
      return;
    }
    if (referenceNeeded && !referenceNo.trim()) {
      setError("Reference number is required for non-cash withdrawals.");
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const res = await memberService.withdrawAdvance(member.id, {
        amount,
        mode,
        referenceNo: referenceNo.trim() || undefined,
        withdrawnOn,
        remarks: remarks.trim() || undefined,
      });

      toast.success("Cashout successful", `₹${amount.toLocaleString("en-IN")} withdrawn from ${member.fullName}'s account.`);
      onSuccess(res.transaction, res.member);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not process withdrawal.");
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Withdraw / Cashout Money"
      description={`Withdraw funds from ${member.fullName}'s advance account balance.`}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<i className="ri-hand-coin-line" />}
            loading={loading}
            disabled={currentBalance <= 0}
            onClick={handleSubmit}
          >
            Confirm Cashout
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Available Balance Box */}
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50/70 p-3 text-sm">
          <span className="text-amber-800 font-medium">Available to Withdraw</span>
          <span className="font-bold text-amber-950">
            <CurrencyDisplay amount={currentBalance} />
          </span>
        </div>

        {currentBalance <= 0 ? (
          <div className="rounded-md bg-danger-50 p-3 text-xs text-danger-700">
            This member has ₹0.00 in their account balance and cannot make a withdrawal.
          </div>
        ) : (
          <>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-foreground-700">Withdrawal Amount</label>
                <button
                  type="button"
                  onClick={() => setAmount(currentBalance)}
                  className="text-xs font-semibold text-primary-600 hover:underline cursor-pointer"
                >
                  Withdraw full balance
                </button>
              </div>
              <CurrencyInput
                label=""
                value={amount}
                onChange={setAmount}
                placeholder="0.00"
                required
                autoFocus
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label="Payment Mode"
                value={mode}
                onChange={(v) => setMode(v as PaymentMode)}
                options={WITHDRAW_MODES}
              />
              <TextField
                label={referenceNeeded ? "Reference / Cheque No" : "Reference (Optional)"}
                placeholder={referenceNeeded ? "Required" : "Optional"}
                value={referenceNo}
                onChange={(e) => setReferenceNo(e.target.value)}
              />
            </div>

            <DatePicker label="Withdrawal Date" value={withdrawnOn} onChange={setWithdrawnOn} />

            <Textarea
              label="Remarks (Optional)"
              placeholder="e.g. Member requested refund of unused advance"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={2}
            />

            {error ? <p className="text-xs text-danger-600">{error}</p> : null}

            <div className="rounded-md bg-background-100/70 p-2.5 text-xs text-foreground-600">
              <i className="ri-information-line mr-1 text-foreground-500" />
              A Withdrawal Voucher (WTH-*) will be generated upon confirmation and can be printed as an official cashout voucher.
            </div>
          </>
        )}
      </form>
    </Modal>
  );
}
